import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getPendingReports } from '../api/reportApi';
import Pagination from '../components/Pagination';

const ReportList = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notification, setNotification] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [filterColumn, setFilterColumn] = useState('id');
  const [filterValue, setFilterValue] = useState('');
  const [filteredReports, setFilteredReports] = useState([]);
  const [allReports, setAllReports] = useState([]);

  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const notificationTimerRef = useRef(null);
  const shouldReconnectRef = useRef(true);
  const { t, i18n } = useTranslation();
  const isZh = i18n.language.startsWith('zh');
  const location = useLocation();

  const filterOptions = [
    { value: 'id', label: t('reports.reportId') },
    { value: 'visit_id', label: t('reports.visitId') },
    { value: 'patient_name', label: t('reports.patientName') },
    { value: 'clinic_name', label: t('reports.clinicName') },
    { value: 'report_type', label: t('reports.reportType') },
    { value: 'visit_type', label: isZh ? '就诊类型' : 'Visit type' }
  ];

  const filterReports = (data, column, value) => {
    if (!value || value.trim() === '') {
      return data;
    }

    return data.filter((report) => {
      const fieldValue = report[column];
      if (fieldValue === null || fieldValue === undefined) {
        return false;
      }

      return String(fieldValue).toLowerCase().includes(value.toLowerCase().trim());
    });
  };

  const paginateData = (data, page, size) => {
    const safeTotalPages = Math.max(1, Math.ceil(data.length / size));
    const safePage = Math.min(page, safeTotalPages);
    const startIndex = (safePage - 1) * size;
    const endIndex = startIndex + size;

    return {
      currentPage: safePage,
      totalPages: safeTotalPages,
      totalCount: data.length,
      rows: data.slice(startIndex, endIndex)
    };
  };

  const getPresetFilterFromSearch = (search) => {
    const params = new URLSearchParams(search);
    const visitID = params.get('visit_id');
    if (visitID) {
      return {
        column: 'visit_id',
        value: visitID
      };
    }

    return {
      column: 'id',
      value: ''
    };
  };

  const buildReviewedVisitLink = (visitId) => `/reviewed-reports?visit_id=${visitId}`;

  const fetchReports = async (page = currentPage, size = pageSize, nextFilterColumn = filterColumn, nextFilterValue = filterValue) => {
    try {
      setLoading(true);
      const allData = await getPendingReports(1, 1000);
      const data = allData || [];
      const filtered = filterReports(data, nextFilterColumn, nextFilterValue);
      const pageData = paginateData(filtered, page, size);

      setAllReports(data);
      setFilteredReports(filtered);
      setReports(pageData.rows);
      setCurrentPage(pageData.currentPage);
      setPageSize(size);
      setTotalCount(pageData.totalCount);
      setTotalPages(pageData.totalPages);
      setFilterColumn(nextFilterColumn);
      setFilterValue(nextFilterValue);
      setError('');
    } catch (requestError) {
      console.error('获取报告列表失败:', requestError);
      setError(`${t('reports.fetchError')}: ${requestError.response?.data?.error || t('common.unknownError')}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    const filtered = filterReports(allReports, filterColumn, filterValue);
    const pageData = paginateData(filtered, 1, pageSize);

    setFilteredReports(filtered);
    setReports(pageData.rows);
    setCurrentPage(pageData.currentPage);
    setTotalCount(pageData.totalCount);
    setTotalPages(pageData.totalPages);
  };

  const handleReset = () => {
    fetchReports(1, pageSize, filterColumn, '');
  };

  const handlePageChange = (page) => {
    const pageData = paginateData(filteredReports, page, pageSize);
    setReports(pageData.rows);
    setCurrentPage(pageData.currentPage);
  };

  const handlePageSizeChange = (nextPageSize) => {
    const pageData = paginateData(filteredReports, 1, nextPageSize);
    setReports(pageData.rows);
    setPageSize(nextPageSize);
    setCurrentPage(pageData.currentPage);
    setTotalPages(pageData.totalPages);
  };

  const showNotification = (message) => {
    setNotification(message);
    window.clearTimeout(notificationTimerRef.current);
    notificationTimerRef.current = window.setTimeout(() => {
      setNotification('');
    }, 3000);
  };

  const connectWebSocket = () => {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/ws`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: 'doctor' }));
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'new_report') {
          showNotification(t('reports.newReportNotification'));
          fetchReports(currentPage, pageSize, filterColumn, filterValue);
        }
      } catch (parseError) {
        console.error('处理WebSocket消息失败:', parseError);
      }
    };

    ws.onclose = () => {
      if (!shouldReconnectRef.current) {
        return;
      }

      reconnectTimerRef.current = window.setTimeout(() => {
        connectWebSocket();
      }, 5000);
    };

    ws.onerror = (socketError) => {
      console.error('WebSocket错误:', socketError);
    };
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    shouldReconnectRef.current = true;
    connectWebSocket();

    return () => {
      shouldReconnectRef.current = false;
      window.clearTimeout(reconnectTimerRef.current);
      window.clearTimeout(notificationTimerRef.current);
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const preset = getPresetFilterFromSearch(location.search);
    fetchReports(1, 10, preset.column, preset.value);
  }, [location.search]);

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleString(isZh ? 'zh-CN' : 'en-US');

  const getReportTypeDisplay = (reportType) => {
    if (!reportType) {
      return t('reports.unknownType');
    }

    const type = reportType.toLowerCase();

    if (type === 'proposal' || type === '建议' || type === '初步') {
      return t('reports.proposalReport');
    }

    if (type === 'final' || type === '最终' || type === '终结') {
      return t('reports.finalReport');
    }

    return reportType.charAt(0).toUpperCase() + reportType.slice(1);
  };

  const getVisitTypeDisplay = (visitType) => {
    if (visitType === 'follow_up') {
      return isZh ? '复诊' : 'Follow-up';
    }
    if (visitType === 'initial') {
      return isZh ? '初诊' : 'Initial';
    }
    if (visitType === 'checkup') {
      return isZh ? '体检' : 'Checkup';
    }
    return isZh ? '未标记' : 'Unspecified';
  };

  const getRelatedVisitsDisplay = (relatedVisitIds) => {
    if (!relatedVisitIds || relatedVisitIds.length === 0) {
      return isZh ? '无关联来源' : 'No linked source visits';
    }

    return isZh
      ? `关联 Visit: ${relatedVisitIds.map((visitId) => `#${visitId}`).join(' / ')}`
      : `Related visits: ${relatedVisitIds.map((visitId) => `#${visitId}`).join(' / ')}`;
  };

  return (
    <div className="stack">
      <div className="page-header">
        <div className="page-title-block">
          <span className="page-eyebrow">{t('dashboard.pendingReports')}</span>
          <h1 className="page-title">{t('reports.pendingReports')}</h1>
          <p className="page-subtitle">
            {isZh
              ? '统一查看当前待审核报告，保留实时推送提醒，并把筛选、分页和操作收敛到同一工作区。'
              : 'Review pending reports in one unified queue with live notifications, focused filters, and a cleaner action surface.'}
          </p>
        </div>

        <div className="page-actions">
          <button type="button" className="theme-button theme-button--secondary" onClick={() => fetchReports(1, pageSize, filterColumn, filterValue)}>
            {t('common.refresh')}
          </button>
        </div>
      </div>

      {notification && (
        <div className="status-banner status-banner--success">
          <span>{notification}</span>
        </div>
      )}

      {error && (
        <div className="status-banner status-banner--error">
          <span>{error}</span>
        </div>
      )}

      <section className="surface-panel">
        <div className="surface-panel__body stack">
          <div className="toolbar-cluster">
            <span className="status-pill status-pill--success">
              {isZh ? `待审核 ${totalCount} 条` : `${totalCount} pending`}
            </span>
            <span className="status-pill status-pill--neutral">
              {isZh ? `第 ${currentPage} / ${totalPages} 页` : `Page ${currentPage} / ${totalPages}`}
            </span>
          </div>

          <div className="filter-row">
            <div className="field">
              <label className="field__label" htmlFor="pending-filter-column">
                {t('common.filterBy')}
              </label>
              <select
                id="pending-filter-column"
                className="theme-select"
                value={filterColumn}
                onChange={(event) => setFilterColumn(event.target.value)}
              >
                {filterOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label className="field__label" htmlFor="pending-filter-value">
                {t('common.search')}
              </label>
              <input
                id="pending-filter-value"
                className="theme-input"
                type="text"
                value={filterValue}
                onChange={(event) => setFilterValue(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    handleSearch();
                  }
                }}
                placeholder={t('common.enterSearchValue')}
              />
            </div>

            <button type="button" className="theme-button theme-button--primary" onClick={handleSearch}>
              {t('common.search')}
            </button>
            <button type="button" className="theme-button theme-button--ghost" onClick={handleReset}>
              {t('common.reset')}
            </button>
          </div>

          {loading ? (
            <div className="loading-state">
              <div className="loading-orb" />
              <span>{t('common.loading')}</span>
            </div>
          ) : reports.length === 0 ? (
            <div className="empty-state">
              <h3 className="auth-form__title">{t('reports.noReports')}</h3>
              <p className="auth-form__subtitle">
                {isZh
                  ? '当前筛选条件下没有可处理的报告。你可以刷新或重置筛选后再试。'
                  : 'There are no reports to review under the current filter. Refresh or reset the filters to check again.'}
              </p>
            </div>
          ) : (
            <div className="data-table-shell">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('reports.reportId')}</th>
                    <th>{t('reports.patientName')}</th>
                    <th>{t('reports.clinicName')}</th>
                    <th>{t('reports.reportType')}</th>
                    <th>{t('reports.createdAt')}</th>
                    <th>{t('reports.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((report) => (
                    <tr key={report.id}>
                      <td>
                        <div className="table-title">#{report.id}</div>
                        <div className="table-meta">{t('reports.visitId')}: {report.visit_id}</div>
                        <div className="table-meta">{getRelatedVisitsDisplay(report.related_visit_ids)}</div>
                        {report.related_visit_ids?.length > 0 && (
                          <div className="visit-chip-list">
                            {report.related_visit_ids.map((visitId) => (
                              <Link key={`${report.id}-${visitId}`} to={buildReviewedVisitLink(visitId)} className="visit-chip">
                                #{visitId}
                              </Link>
                            ))}
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="table-title">{report.patient_name || t('common.unknown')}</div>
                      </td>
                      <td>
                        <div className="table-title">{report.clinic_name || t('reports.notSpecified')}</div>
                        <div className="table-meta">{t('reports.clinicId')}: {report.clinic_id || t('reports.notSpecified')}</div>
                      </td>
                      <td>
                        <span className="status-pill status-pill--warning">
                          {getReportTypeDisplay(report.report_type)}
                        </span>
                        <div style={{ marginTop: '0.5rem' }}>
                          <span className={`status-pill ${report.visit_type === 'follow_up' ? 'status-pill--success' : 'status-pill--neutral'}`}>
                            {getVisitTypeDisplay(report.visit_type)}
                          </span>
                        </div>
                      </td>
                      <td>{formatDate(report.created_at)}</td>
                      <td>
                        <div className="table-actions">
                          <Link
                            to={`/reports/${report.id}/review`}
                            className="theme-button theme-button--primary"
                          >
                            {t('reports.review')}
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                pageSize={pageSize}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ReportList;
